import styled from 'styled-components'
import { SvgTick } from '../SvgImports'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '../../utils/useClient'
import { useAppSelector } from '../../store/hooks'
import {
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../store/appSlice'
import {
  getMarketDenomination,
  getSessionInitDataState,
  getStartupData,
} from '../../store/sessionDataSlice'
import {
  TdsButton,
  TdsIcon,
  TdsMessage,
  TdsSpinner,
  TdsTextField,
} from '@scania/tegel-react'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeShareOption,
  ScaniaAdobeTrackingShareEmailEvent,
  ScaniaAdobeTrackingShareFacebookEvent,
  ScaniaAdobeTrackingShareLinkEvent,
  ScaniaAdobeTrackingShareWhatsAppEvent,
  pushAdobeEvent,
} from '../../utils/adobeAnalytics'
import {
  buildAbsoluteFacebookShareLink,
  buildAbsolutePublicConfigLink,
} from '../../utils/UrlBuilders'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import {
  Recipient,
  SendShareEmailParamsShareEmailInfo,
} from '../../api/generated'
import { SESSION_FAILURE } from '../../api/errors'
import { GvShareEmailWidthColumnBreakpoint } from '../../utils/constants'
import { useRfqEysButtonState } from '../../pages/GuidedView/RfqEysButtonState'
import { TestElementTypeId } from '../../types/TestAttributeId'

const ContentRoot = styled.div``

const Header = styled.h2`
  margin-bottom: 16px;
`

const Text = styled.p``

interface CardProps {
  $isSelected: boolean
}

const ShareCard = styled.div<CardProps>`
  border: ${(props) =>
    props.$isSelected
      ? '2px solid var(--tds-information)'
      : '1px solid var(--tds-grey-300)'};
  border-radius: 6px;
  padding: 8px;
  padding-left: 24px;
  margin-bottom: 16px;
`

const CardSelection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const CardTextWrapper = styled.div``

const CardHeader = styled.div`
  font-size: 16px;
  padding-bottom: 4px;
`

const CardText = styled.div`
  font-size: 12px;
`

const CardContent = styled.div`
  margin-top: 16px;
`

const ShareEmailCardContent = styled.div`
  margin-top: 16px;
  display: flex;

  form {
    @media screen and (max-width: ${GvShareEmailWidthColumnBreakpoint}px) {
      display: flex;
      flex-direction: column;
    }
  }
`

const TextAreaExpandable = styled.textarea`
  border: none;
  outline: none;
  background-color: var(--tds-textarea-background);
  color: var(--tds-textarea-color);
  border-radius: 4px 4px 0 0;
  font: var(--tds-detail-02);
  resize: none;
  overflow: hidden;
  height: auto;
  min-height: 80px;
  width: 100%;
  padding: 8px;
  border-bottom: 1px solid var(--tds-textarea-border-bottom);
  transition: border-bottom 200ms ease;

  &:focus {
    border-bottom: 2px solid var(--tds-information);
  }

  :active {
    border-bottom: 2px solid var(--tds-information);
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 32px;
  height: 32px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 8px;
`

interface SpinnerProps {
  $showSpinner: boolean
}

const SpinnerWrapper = styled.div<SpinnerProps>`
  display: ${(props) => (props.$showSpinner ? '' : 'none')};
  margin: 16px;
  height: 40px;

  svg {
    color: var(--tds-blue-600);
  }
`

const ShareEmailColumn = styled.div`
  width: 46%;
  display: inline-block;
  vertical-align: top;
  margin-right: 8px;
  margin-bottom: 8px;

  @media screen and (max-width: ${GvShareEmailWidthColumnBreakpoint}px) {
    width: 100%;
  }
`

const ShareEmailColumnHeader = styled.div`
  font-weight: bold;
`

const LabelInputField = styled.div`
  margin-top: 16px;
  display: inline-block;
`

const LabelCompulsory = styled.div`
  display: inline-block;
  color: red;
  margin-left: 6px;
  font-size: 16px;
  font-weight: bold;
`

const LinkWrapper = styled.div`
  width: 100%;
  word-wrap: break-word;
  margin-bottom: 8px;
`

const EysHeader = styled.h5`
  margin-bottom: 16px;
  margin-top: 16px;
`

enum ShareTypes {
  link,
  email,
  whatsApp,
  facebook,
  none,
}

interface ContentShareProps {
  handleFatalError: () => void
  handleSessionFailure: () => void
  handleRfqOrEysClick: () => void
}

export function ContentShare({
  handleFatalError,
  handleSessionFailure,
  handleRfqOrEysClick,
}: ContentShareProps): JSX.Element {
  const client = useClient()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const t2 = useTextsV2()
  const marketDenomination = useAppSelector(getMarketDenomination)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const exploreYourScaniaMode =
    useAppSelector(getStartupData)?.exploreYourScaniaMode
  const { rfqOrEysButtonEnabled } = useRfqEysButtonState()

  const [showSpinner, setShowSpinner] = useState<boolean>(false)
  const [selectedShareType, setSelectedShareType] = useState<ShareTypes | null>(
    null,
  )

  const [enteredFirstNameSender, setEnteredFirstNameSender] =
    useState<string>('')
  const [enteredSurnameSender, setEnteredSurnameSender] = useState<string>('')
  const [enteredEmailSender, setEnteredEmailSender] = useState<string>('')
  const [enteredNameReciever, setEnteredNameReciever] = useState<string>('')
  const [enteredEmailReciever, setEnteredEmailReciever] = useState<string>('')
  const [enteredMessage, setEnteredMessage] = useState<string>('')

  const [enteredFirstNameErrorState, setEnteredFirstNameErrorState] = useState<
    'default' | 'error' | 'success' | undefined
  >(undefined)
  const [enteredSurNameErrorState, setEnteredSurNameErrorState] = useState<
    'default' | 'error' | 'success' | undefined
  >(undefined)
  const [enteredEmailSenderErrorState, setEnteredEmailSenderErrorState] =
    useState<'default' | 'error' | 'success' | undefined>(undefined)
  const [enteredRecieverNameErrorState, setEnteredRecieverNameErrorState] =
    useState<'default' | 'error' | 'success' | undefined>(undefined)
  const [enteredEmailRecieverErrorState, setEnteredEmailRecieverErrorState] =
    useState<'default' | 'error' | 'success' | undefined>(undefined)

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')

  const [shareLink, setShareLink] = useState<string | null>(null)
  const [shareLinkWhatsApp, setShareLinkWhatsApp] = useState<string | null>(
    null,
  )
  const [facebookUrl, setFacebookUrl] = useState<string | null>(null)

  const handleSelectShareClick = (shareType: ShareTypes) => {
    setSelectedShareType(shareType)
    setSuccessMessage('')
    setErrorMessage('')

    if (shareType === ShareTypes.link) {
      const trackingEvent: ScaniaAdobeTrackingShareLinkEvent = {
        event: ScaniaAdobeEventId.Share,
        eventInfo: {
          shareOption: ScaniaAdobeShareOption.CopyLink,
        },
      }
      pushAdobeEvent(trackingEvent)
    }
  }

  const copyTextToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(shareLink || '')
    setSelectedShareType(ShareTypes.none)
  }

  const emailSenderChangeHandler = (event: any) => {
    const newEmail = event.target.value.trim().toLowerCase()
    const validFormat =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+.)+[^<>()[\].,;:\s@"]{2,})$/i
    if (!newEmail) {
      setEnteredEmailSender('')
      setEnteredEmailSenderErrorState('error')
    } else if (!validFormat.test(newEmail)) {
      setEnteredEmailSender(newEmail)
      setEnteredEmailSenderErrorState('error')
    } else {
      setEnteredEmailSender(newEmail)
      setEnteredEmailSenderErrorState('success')
    }
  }

  const firstNameSenderChangeHandler = (event: any) => {
    const value = event.target.value
    const valueTrimmed = typeof value === 'string' ? value.trim() : null
    if (!valueTrimmed) {
      setEnteredFirstNameSender('')
      setEnteredFirstNameErrorState('error')
    } else {
      setEnteredFirstNameSender(valueTrimmed)
      setEnteredFirstNameErrorState('success')
    }
  }

  const surnameSenderChangeHandler = (event: any) => {
    const value = event.target.value
    const valueTrimmed = typeof value === 'string' ? value.trim() : null
    if (!valueTrimmed) {
      setEnteredSurnameSender('')
      setEnteredSurNameErrorState('error')
    } else {
      setEnteredSurnameSender(valueTrimmed)
      setEnteredSurNameErrorState('success')
    }
  }

  const nameRecieverChangeHandler = (event: any) => {
    const value = event.target.value
    const valueTrimmed = typeof value === 'string' ? value.trim() : null
    if (!valueTrimmed) {
      setEnteredNameReciever('')
      setEnteredRecieverNameErrorState('error')
    } else {
      setEnteredNameReciever(valueTrimmed)
      setEnteredRecieverNameErrorState('success')
    }
  }

  const emailRecieverChangeHandler = (event: any) => {
    const newEmail = event.target.value.trim().toLowerCase() // TODO - remove toLowerCase?
    const validFormat =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+.)+[^<>()[\].,;:\s@"]{2,})$/i
    if (!newEmail) {
      setEnteredEmailReciever('')
      setEnteredEmailRecieverErrorState('error')
    } else if (!validFormat.test(newEmail)) {
      setEnteredEmailReciever(newEmail)
      setEnteredEmailRecieverErrorState('error')
    } else {
      setEnteredEmailReciever(newEmail)
      setEnteredEmailRecieverErrorState('success')
    }
  }

  const messageChangeHandler = (event: any) => {
    event.target.style.height = `${event.target.scrollHeight}px`
    if (!event.target.value) {
      setEnteredMessage('')
    } else {
      setEnteredMessage(event.target.value)
    }
  }

  const checkCompulsoryFields = () => {
    let correctInput = false
    if (
      enteredFirstNameErrorState === 'success' &&
      enteredSurNameErrorState === 'success' &&
      enteredEmailSenderErrorState === 'success' &&
      enteredEmailRecieverErrorState === 'success' &&
      enteredRecieverNameErrorState === 'success'
    ) {
      setErrorMessage('')
      correctInput = true
    } else {
      setErrorMessage(t2(TextId.LABEL_INVALID_FORM_INPUT))
      if (enteredFirstNameErrorState !== 'success') {
        setEnteredFirstNameErrorState('error')
      }
      if (enteredSurNameErrorState !== 'success') {
        setEnteredSurNameErrorState('error')
      }
      if (enteredEmailSenderErrorState !== 'success') {
        setEnteredEmailSenderErrorState('error')
      }
      if (enteredEmailRecieverErrorState !== 'success') {
        setEnteredEmailRecieverErrorState('error')
      }
      if (enteredRecieverNameErrorState !== 'success') {
        setEnteredRecieverNameErrorState('error')
      }
    }
    return correctInput
  }

  const sendEmailSubmitHandler = async (event: any) => {
    event.preventDefault()
    if (!sessionInitData || !client || !marketLanguage) {
      return
    }
    if (!checkCompulsoryFields()) {
      return
    }
    const trackingEvent: ScaniaAdobeTrackingShareEmailEvent = {
      event: ScaniaAdobeEventId.Share,
      eventInfo: {
        shareOption: ScaniaAdobeShareOption.Email,
      },
    }
    pushAdobeEvent(trackingEvent)

    setShowSpinner(true)

    const sessionId = sessionInitData.sessionId
    const recipient: Recipient = {
      name: enteredNameReciever,
      email: enteredEmailReciever,
    }
    const info: SendShareEmailParamsShareEmailInfo = {
      recipients: [recipient],
      personalMessage: enteredMessage,
      firstName: enteredFirstNameSender,
      familyName: enteredSurnameSender,
      userEmail: enteredEmailSender,
    }

    try {
      await client.sendShareMail({
        sessionId,
        shareEmailInfo: info,
        language: marketLanguage.language.toString(),
      })
      setSuccessMessage(t2(TextId.DESCRIPTION_SHARE_EMAIL_SENT))
      setShowSpinner(false)
      setSelectedShareType(ShareTypes.none)
    } catch (err) {
      console.error(err)
      console.error(t2(TextId.ERROR_SERVER_COMMUNICATION_BREAKDOWN))
      setErrorMessage(t2(TextId.ERROR_SERVER_COMMUNICATION_BREAKDOWN))
      setShowSpinner(false)
      return
    }

    setEnteredFirstNameSender('')
    setEnteredSurnameSender('')
    setEnteredEmailSender('')
    setEnteredNameReciever('')
    setEnteredEmailReciever('')
    setEnteredMessage('')
    setEnteredEmailRecieverErrorState(undefined)
    setEnteredEmailSenderErrorState(undefined)
    setEnteredFirstNameErrorState(undefined)
    setEnteredRecieverNameErrorState(undefined)
    setEnteredSurNameErrorState(undefined)
    setShowSpinner(false)
  }

  // Create link to share
  useEffect(() => {
    async function getPublicConfUrl() {
      if (!sessionInitData || !client || !marketDenomination) {
        return
      }
      if (!marketLanguage) {
        return
      }
      const configAux = {}
      const response = await client.publicConfigV2Save(
        sessionInitData.sessionId,
        marketDenomination,
        configAux,
      )
      if (response.error) {
        console.error(
          'Failed to save public config for share link, status: ' +
            response.error,
        )
        if (response.error === SESSION_FAILURE) {
          handleSessionFailure()
        } else {
          handleFatalError()
        }
        return
      }
      if (!response.publicConfigV2Save) {
        console.error('Expected response.publicConfigV2Save to be defined.')
        handleFatalError()
        return
      }
      const guid = response.publicConfigV2Save.guid
      const linkFacebook = buildAbsoluteFacebookShareLink(marketLanguage, guid)
      setFacebookUrl(linkFacebook)

      const config = response.publicConfigV2Save
      const linkToPublicConfig = buildAbsolutePublicConfigLink(
        marketLanguage,
        config.guid,
      )
      if (!linkToPublicConfig) {
        console.error('Failed to save share link.')
        handleFatalError()
        return
      }
      setShareLink(linkToPublicConfig)
      const linkWhatsApp =
        'https://wa.me/?text=' + encodeURIComponent(linkToPublicConfig)
      setShareLinkWhatsApp(linkWhatsApp)
    }

    getPublicConfUrl()
  }, [
    client,
    marketLanguage,
    sessionInitData,
    marketDenomination,
    handleSessionFailure,
    handleFatalError,
  ])

  const handleShareTrackingWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation()
    const trackingEvent: ScaniaAdobeTrackingShareWhatsAppEvent = {
      event: ScaniaAdobeEventId.Share,
      eventInfo: {
        shareOption: ScaniaAdobeShareOption.WhatsApp,
      },
    }
    pushAdobeEvent(trackingEvent)
    setSelectedShareType(ShareTypes.none)
  }

  const handleShareToFacebookClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!facebookUrl) {
        return
      }
      const trackingEvent: ScaniaAdobeTrackingShareFacebookEvent = {
        event: ScaniaAdobeEventId.Share,
        eventInfo: {
          shareOption: ScaniaAdobeShareOption.Facebook,
        },
      }
      pushAdobeEvent(trackingEvent)
      FB.ui(
        {
          method: 'share',
          href: facebookUrl,
        },
        function (response) {},
      )
      setSelectedShareType(ShareTypes.none)
    },
    [facebookUrl],
  )

  return (
    <ContentRoot>
      <SpinnerWrapper $showSpinner={showSpinner}>
        <TdsSpinner size="sm" />
      </SpinnerWrapper>
      <Header>{t2(TextId.LABEL_ACTION_SHARE)}</Header>
      <Text>{t2(TextId.SIDEPANEL_DESCRIPTION_SHARE)}</Text>

      {exploreYourScaniaMode && (
        <div>
          <EysHeader>
            {t2(TextId.EYS_SHARE_MENU_SALES_REPRESENTATIVE)}
          </EysHeader>
          <TdsButton
            size="md"
            text={t2(TextId.EYS_SEND_CHANGES_BUTTON)}
            onClick={handleRfqOrEysClick}
            disabled={!rfqOrEysButtonEnabled}
          >
            <TdsIcon slot="icon" name="send" size="20px" />
          </TdsButton>
          <EysHeader>{t2(TextId.EYS_SHARE_MENU_OTHERS)}</EysHeader>
        </div>
      )}

      {marketSettings?.shareLinkEnabled && (
        <ShareCard
          $isSelected={selectedShareType === ShareTypes.link}
          onClick={() => handleSelectShareClick(ShareTypes.link)}
          data-test-element-type={TestElementTypeId.SidePanelShareLinkCard}
        >
          <CardSelection>
            <CardTextWrapper>
              <CardHeader>{t2(TextId.LABEL_ACTION_SHARE_LINK)}</CardHeader>
              <CardText>{t2(TextId.SIDEPANEL_DESCRIPTION_SHARE_LINK)}</CardText>
            </CardTextWrapper>
            <IconWrapper>
              {selectedShareType === ShareTypes.link && (
                <SvgTick height="24px" />
              )}
            </IconWrapper>
          </CardSelection>
          {selectedShareType === ShareTypes.link && (
            <CardContent>
              {shareLink ? (
                <LinkWrapper className="tds-detail-05">{shareLink}</LinkWrapper>
              ) : (
                <SpinnerWrapper $showSpinner={true}>
                  <TdsSpinner size="sm" />
                </SpinnerWrapper>
              )}
            </CardContent>
          )}
          {selectedShareType === ShareTypes.link && (
            <ButtonWrapper>
              <TdsButton
                variant="primary"
                size="md"
                onClick={copyTextToClipboard}
                text={t2(TextId.LABEL_ACTION_COPY)}
                disabled={showSpinner}
                fullbleed
                style={{ width: '160px' }}
                data-test-element-type={
                  TestElementTypeId.SidePanelShareLinkButton
                }
              />
            </ButtonWrapper>
          )}
        </ShareCard>
      )}

      {marketSettings?.shareEmailEnabled && (
        <ShareCard
          $isSelected={selectedShareType === ShareTypes.email}
          onClick={() => handleSelectShareClick(ShareTypes.email)}
          data-test-element-type={TestElementTypeId.SidePanelShareEmailCard}
        >
          <CardSelection>
            <CardTextWrapper>
              <CardHeader>{t2(TextId.SIDEPANEL_SUBHEADER_EMAIL)}</CardHeader>
              <CardText>
                {t2(TextId.SIDEPANEL_DESCRIPTION_SHARE_EMAIL)}
              </CardText>
            </CardTextWrapper>
            <IconWrapper>
              {selectedShareType === ShareTypes.email && (
                <SvgTick height="24px" />
              )}
            </IconWrapper>
          </CardSelection>
          {selectedShareType === ShareTypes.email && (
            <ShareEmailCardContent>
              {!successMessage && (
                <form onSubmit={sendEmailSubmitHandler}>
                  <ShareEmailColumn>
                    <ShareEmailColumnHeader>
                      {t2(TextId.LABEL_SHARE_SENDER)}
                    </ShareEmailColumnHeader>
                    <LabelInputField>
                      {t2(TextId.LABEL_FIRST_NAME)}
                    </LabelInputField>
                    <LabelCompulsory>*</LabelCompulsory>
                    <TdsTextField
                      type="text"
                      onInput={firstNameSenderChangeHandler}
                      state={enteredFirstNameErrorState}
                      value={enteredFirstNameSender}
                      disabled={showSpinner}
                      size="md"
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormSenderFirstNameInput
                      }
                    />
                    <LabelInputField>
                      {t2(TextId.LABEL_LAST_NAME)}
                    </LabelInputField>
                    <LabelCompulsory>*</LabelCompulsory>
                    <TdsTextField
                      type="text"
                      onInput={surnameSenderChangeHandler}
                      state={enteredSurNameErrorState}
                      value={enteredSurnameSender}
                      disabled={showSpinner}
                      size="md"
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormSenderLastNameInput
                      }
                    />
                    <LabelInputField>{t2(TextId.LABEL_EMAIL)}</LabelInputField>
                    <LabelCompulsory>*</LabelCompulsory>
                    <TdsTextField
                      type="text"
                      onInput={emailSenderChangeHandler}
                      state={enteredEmailSenderErrorState}
                      value={enteredEmailSender}
                      disabled={showSpinner}
                      size="md"
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormSenderEmailInput
                      }
                    />
                  </ShareEmailColumn>

                  <ShareEmailColumn>
                    <ShareEmailColumnHeader>
                      {t2(TextId.LABEL_SHARE_RECIPIENT)}
                    </ShareEmailColumnHeader>
                    <LabelInputField>{t2(TextId.LABEL_NAME)}</LabelInputField>
                    <LabelCompulsory>*</LabelCompulsory>
                    <TdsTextField
                      type="text"
                      onInput={nameRecieverChangeHandler}
                      state={enteredRecieverNameErrorState}
                      value={enteredNameReciever}
                      disabled={showSpinner}
                      size="md"
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormRecipientNameInput
                      }
                    />
                    <LabelInputField>{t2(TextId.LABEL_EMAIL)}</LabelInputField>
                    <LabelCompulsory>*</LabelCompulsory>
                    <TdsTextField
                      type="text"
                      onInput={emailRecieverChangeHandler}
                      state={enteredEmailRecieverErrorState}
                      value={enteredEmailReciever}
                      disabled={showSpinner}
                      size="md"
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormRecipientEmailInput
                      }
                    />
                    <LabelInputField>
                      {t2(TextId.LABEL_PERSONAL_MESSAGE)}
                    </LabelInputField>
                    <TextAreaExpandable
                      onInput={messageChangeHandler}
                      value={enteredMessage}
                      disabled={showSpinner}
                      data-test-element-type={
                        TestElementTypeId.SidePanelShareEmailFormPersonalMessageInput
                      }
                    />
                  </ShareEmailColumn>

                  <SpinnerWrapper $showSpinner={showSpinner}>
                    <TdsSpinner size="sm" />
                  </SpinnerWrapper>
                </form>
              )}
            </ShareEmailCardContent>
          )}
          {selectedShareType === ShareTypes.email && (
            <ButtonWrapper style={{ marginBottom: '8px' }}>
              <TdsButton
                variant="primary"
                size="md"
                text={t2(TextId.LABEL_ACTION_SHARE)}
                onClick={sendEmailSubmitHandler}
                disabled={showSpinner}
                fullbleed
                style={{ width: '160px' }}
                data-test-element-type={
                  TestElementTypeId.SidePanelShareEmailSubmitButton
                }
              />
            </ButtonWrapper>
          )}
          {errorMessage && (
            <TdsMessage
              variant="error"
              header={errorMessage}
              style={{ marginTop: '8px' }}
            />
          )}
        </ShareCard>
      )}

      {marketSettings?.shareWhatsappEnabled && (
        <ShareCard
          $isSelected={selectedShareType === ShareTypes.whatsApp}
          onClick={() => handleSelectShareClick(ShareTypes.whatsApp)}
          data-test-element-type={TestElementTypeId.SidePanelShareWhatsAppCard}
        >
          <CardSelection>
            <CardTextWrapper>
              <CardHeader>{t2(TextId.LABEL_ACTION_SHARE_WHATSAPP)}</CardHeader>
              <CardText>
                {t2(TextId.SIDEPANEL_DESCRIPTION_SHARE_WHATSAPP)}
              </CardText>
            </CardTextWrapper>
            <IconWrapper>
              {selectedShareType === ShareTypes.whatsApp && (
                <SvgTick height="24px" />
              )}
            </IconWrapper>
          </CardSelection>
          {selectedShareType === ShareTypes.whatsApp && (
            <CardContent>
              {shareLinkWhatsApp ? (
                <LinkWrapper className="tds-detail-05">
                  {shareLinkWhatsApp}
                </LinkWrapper>
              ) : (
                <SpinnerWrapper $showSpinner={true}>
                  <TdsSpinner size="sm" />
                </SpinnerWrapper>
              )}
            </CardContent>
          )}
          {selectedShareType === ShareTypes.whatsApp && (
            <a
              href={shareLinkWhatsApp || ''}
              target={'_blank'}
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <ButtonWrapper key={selectedShareType.toString()}>
                <TdsButton
                  size="md"
                  onClick={handleShareTrackingWhatsApp}
                  text={t2(TextId.LABEL_ACTION_SHARE)}
                  disabled={shareLinkWhatsApp ? false : true}
                  fullbleed
                  style={{ width: '160px' }}
                  data-test-element-type={
                    TestElementTypeId.SidePanelShareWhatsAppButton
                  }
                />
              </ButtonWrapper>
            </a>
          )}
        </ShareCard>
      )}

      {marketSettings?.shareFacebookEnabled && (
        <ShareCard
          $isSelected={selectedShareType === ShareTypes.facebook}
          onClick={() => handleSelectShareClick(ShareTypes.facebook)}
          data-test-element-type={TestElementTypeId.SidePanelShareFacebookCard}
        >
          <CardSelection>
            <CardTextWrapper>
              <CardHeader>{t2(TextId.SIDEPANEL_SUBHEADER_FACEBOOK)}</CardHeader>
              <CardText>
                {t2(TextId.SIDEPANEL_DESCRIPTION_SHARE_FACEBOOK)}
              </CardText>
            </CardTextWrapper>
            <IconWrapper>
              {selectedShareType === ShareTypes.facebook && (
                <SvgTick height="24px" />
              )}
            </IconWrapper>
          </CardSelection>
          {selectedShareType === ShareTypes.facebook && (
            <CardContent>
              {facebookUrl ? (
                <LinkWrapper className="tds-detail-05">
                  {facebookUrl}
                </LinkWrapper>
              ) : (
                <SpinnerWrapper $showSpinner={true}>
                  <TdsSpinner size="sm" />
                </SpinnerWrapper>
              )}
            </CardContent>
          )}
          {selectedShareType === ShareTypes.facebook && (
            <ButtonWrapper>
              <TdsButton
                variant="primary"
                size="md"
                text={t2(TextId.LABEL_ACTION_SHARE_FACEBOOK)}
                onClick={handleShareToFacebookClick}
                disabled={!facebookUrl}
                data-test-element-type={
                  TestElementTypeId.SidePanelShareFacebookButton
                }
              />
            </ButtonWrapper>
          )}
        </ShareCard>
      )}

      {successMessage && (
        <TdsMessage variant="success" style={{ marginTop: '8px' }}>
          {successMessage}
        </TdsMessage>
      )}
    </ContentRoot>
  )
}
